import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, RotateCcw, Save } from 'lucide-react';
import api from './api';

/**
 * Admin agenda — view two months at a time, click any date to cycle its status:
 *   open (default) -> limited -> booked -> open
 * A small inline note input lets the admin add a reason ("Conference", etc.).
 */
const NEXT_STATUS = { open: 'limited', limited: 'booked', booked: 'open' };
const STATUS_LABEL = { open: 'Open', limited: 'Limited', booked: 'Fully booked' };
const STATUS_STYLES = {
  open: 'bg-[#D4AF37]/15 hover:bg-[#D4AF37]/30 border-[#D4AF37]/35 text-white',
  limited: 'bg-amber-400/20 hover:bg-amber-400/35 border-amber-400/55 text-amber-100',
  booked: 'bg-rose-500/15 hover:bg-rose-500/30 border-rose-400/45 text-rose-100',
};

const isoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);

const buildMonthGrid = (anchor) => {
  const first = startOfMonth(anchor);
  const lastDay = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const startWeekday = (first.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) cells.push(new Date(first.getFullYear(), first.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

export default function AvailabilityPanel() {
  const [anchor, setAnchor] = useState(() => startOfMonth(new Date()));
  const [overrides, setOverrides] = useState({}); // { iso: {status, note} }
  const [busy, setBusy] = useState(null);
  const [selected, setSelected] = useState(null); // iso
  const [noteDraft, setNoteDraft] = useState('');

  const refresh = useCallback(async () => {
    const start = startOfMonth(anchor);
    const end = new Date(anchor.getFullYear(), anchor.getMonth() + 2, 0);
    try {
      const { data } = await api.get('/api/availability', {
        params: { from_date: isoDate(start), to_date: isoDate(end) },
      });
      const map = {};
      (data || []).forEach((row) => { map[row.date] = { status: row.status, note: row.note || '' }; });
      setOverrides(map);
    } catch (_) { /* keep prior */ }
  }, [anchor]);

  useEffect(() => { refresh(); }, [refresh]);

  const monthFmt = useMemo(() => new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' }), []);
  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const cycle = async (iso) => {
    setBusy(iso);
    const current = overrides[iso]?.status || 'open';
    const next = NEXT_STATUS[current];
    try {
      if (next === 'open') {
        await api.delete(`/api/admin/availability/${iso}`);
        setOverrides((m) => {
          const c = { ...m }; delete c[iso]; return c;
        });
      } else {
        const note = (selected === iso ? noteDraft : (overrides[iso]?.note || '')) || '';
        await api.put(`/api/admin/availability/${iso}`, { status: next, note });
        setOverrides((m) => ({ ...m, [iso]: { status: next, note } }));
      }
    } catch (e) {
      alert(e?.response?.data?.detail || 'Update failed');
    } finally { setBusy(null); }
  };

  const saveNote = async () => {
    if (!selected) return;
    const cur = overrides[selected];
    if (!cur) return; // notes only meaningful when there's a status override
    setBusy(selected);
    try {
      await api.put(`/api/admin/availability/${selected}`, { status: cur.status, note: noteDraft });
      setOverrides((m) => ({ ...m, [selected]: { ...cur, note: noteDraft } }));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Update failed');
    } finally { setBusy(null); }
  };

  const reset = async (iso) => {
    setBusy(iso);
    try {
      await api.delete(`/api/admin/availability/${iso}`);
      setOverrides((m) => { const c = { ...m }; delete c[iso]; return c; });
      if (selected === iso) { setSelected(null); setNoteDraft(''); }
    } finally { setBusy(null); }
  };

  const onPick = (iso) => {
    setSelected(iso);
    setNoteDraft(overrides[iso]?.note || '');
  };

  const renderMonth = (offset) => {
    const m = addMonths(anchor, offset);
    const cells = buildMonthGrid(m);
    return (
      <div className="flex-1 min-w-0">
        <div className="text-center mb-3">
          <div className="font-serif-display text-xl text-white">{monthFmt.format(m)}</div>
        </div>
        <div className="grid grid-cols-7 gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/45 mb-2">
          {weekdays.map((wd) => (<div key={wd} className="text-center py-1">{wd}</div>))}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {cells.map((d, idx) => {
            if (!d) return <div key={`e-${idx}`} className="aspect-square" />;
            const iso = isoDate(d);
            const status = overrides[iso]?.status || 'open';
            const isBusy = busy === iso;
            const isSelected = selected === iso;
            return (
              <div key={iso} className="relative">
                <button
                  type="button"
                  data-testid={`adm-avail-cell-${iso}`}
                  data-status={status}
                  onClick={() => { onPick(iso); cycle(iso); }}
                  className={`w-full aspect-square rounded-lg border text-sm font-medium flex flex-col items-center justify-center transition-colors ${STATUS_STYLES[status]} ${isSelected ? 'ring-2 ring-[#D4AF37]' : ''}`}
                >
                  <span>{d.getDate()}</span>
                  <span className="mt-1 text-[9px] uppercase tracking-[0.18em] opacity-80">
                    {isBusy ? '…' : (status === 'open' ? '' : status === 'limited' ? 'limited' : 'full')}
                  </span>
                </button>
                {status !== 'open' && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); reset(iso); }}
                    title="Reset to open"
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#072A1F] border border-[#D4AF37]/40 text-white/85 hover:text-[#D4AF37] flex items-center justify-center"
                  >
                    <RotateCcw className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="availability-panel">
      <div className="bg-[#124D3A] border border-[#D4AF37]/15 rounded-3xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
          <div>
            <h3 className="font-serif-display text-xl text-white">Agenda · Availability</h3>
            <p className="text-sm text-white/60 mt-1">
              Click any date to cycle: <span className="text-[#D4AF37]">Open</span> →
              <span className="text-amber-300"> Limited</span> → <span className="text-rose-300">Fully booked</span> → Open.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAnchor((a) => addMonths(a, -1))}
              data-testid="adm-avail-prev"
              className="w-9 h-9 rounded-full bg-[#072A1F] border border-[#D4AF37]/30 text-white hover:bg-[#D4AF37] hover:text-[#0B3D2E] transition-colors flex items-center justify-center"
              aria-label="Previous"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setAnchor(() => startOfMonth(new Date()))}
              className="text-xs uppercase tracking-[0.2em] px-3 py-1.5 rounded-full bg-[#072A1F] border border-[#D4AF37]/30 text-white/85 hover:text-[#D4AF37]"
            >
              Today
            </button>
            <button
              onClick={() => setAnchor((a) => addMonths(a, 1))}
              data-testid="adm-avail-next"
              className="w-9 h-9 rounded-full bg-[#072A1F] border border-[#D4AF37]/30 text-white hover:bg-[#D4AF37] hover:text-[#0B3D2E] transition-colors flex items-center justify-center"
              aria-label="Next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 mt-4">
          {renderMonth(0)}
          {renderMonth(1)}
        </div>
      </div>

      {/* Note editor for selected date */}
      <div className="bg-[#124D3A] border border-[#D4AF37]/15 rounded-3xl p-6">
        <div className="font-serif-display text-lg text-white mb-1">
          {selected ? `Note · ${selected}` : 'Select a date'}
        </div>
        <p className="text-sm text-white/55 mb-4">
          Optional note shown on hover (e.g. "Surgery week", "Dr. Yuksel away").
          {selected && overrides[selected] && ` Current status: ${STATUS_LABEL[overrides[selected].status]}.`}
        </p>
        <div className="flex flex-col md:flex-row gap-3">
          <input
            type="text"
            placeholder={selected ? 'Add a short note…' : 'Click a date in the calendar first'}
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            disabled={!selected || !overrides[selected]}
            data-testid="adm-avail-note-input"
            className="flex-1 px-5 py-3 rounded-full bg-[#072A1F] border border-[#D4AF37]/25 text-white placeholder:text-white/40 outline-none focus:border-[#D4AF37] disabled:opacity-50"
          />
          <button
            type="button"
            onClick={saveNote}
            disabled={!selected || !overrides[selected] || busy === selected}
            data-testid="adm-avail-note-save"
            className="px-6 py-3 rounded-full bg-[#D4AF37] text-[#0B3D2E] font-semibold flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50"
          >
            {busy === selected ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save note
          </button>
        </div>
      </div>
    </div>
  );
}
